import * as Sentry from "@sentry/browser";
import classNames from "classnames";
import { ButtonSecondary, ButtonTertiary } from "components/Atoms/Button";
import ContactModalPicker from "components/Picker/ContactPicker/ContactModalPicker";
import { SidePanelContext } from "components/SidePanel/SidePanelContext";
import OptionalTextAreaInput from "components/ValidatedFields/OptionalTextAreaInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import TravelClassDropdown from "components/dropdowns/reactselect/TravelClassDropdown";
import ValidatorContext from "formvalidation/FormContext";
import { useValidators } from "formvalidation/hooks";
import React, { useContext, useState } from "react";
import { Message } from "semantic-ui-react";
import { PassengerCardFieldNew } from "./PassengerCardField";

interface Props {
  inSidePanel: boolean
  isPrivateJet: boolean
  flightId: number
  onSaved: () => Promise<void>
  onCancel: () => void
}

interface NewPassangerState {
  contact: server.dto.ContactSmallV2
  travelClass: server.dto.FlightTravelClass
  seat: string
  bookingCode: string
  meal: string
  eTicket: string
  baggage: string
}

export const NewPassengerCard = (props: Props) => {
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = React.useState(false);
  const [state, setState] = React.useState<NewPassangerState>({
    contact: null,
    travelClass: null,
    seat: "",
    bookingCode: "",
    meal: "",
    eTicket: "",
    baggage: "",
  });

  const sidePanelContext = useContext(SidePanelContext);
  const isNarrow = props.inSidePanel && sidePanelContext.layoutType === "narrow";
  const validators = useValidators();

  const onValueChange = (value: Partial<NewPassangerState>) => {
    setState(s => ({ ...s, ...value }));
  }

  const save = async () => {
    if (isSaving) return;
    setIsSaving(true);

    const validatorResult = await validators.validateAll();
    if (validatorResult.hasErrors) {
      setIsSaving(false);
      return;
    }

    try {
      const response = await window.CoreApi.Flight.CreatePassenger({
        baggage: state.baggage,
        bookingReference: state.bookingCode,
        contact: state.contact.contactId,
        flightId: props.flightId,
        meal: state.meal,
        seat: state.seat,
        ticketNumber: state.eTicket,
        travelClass: state.travelClass ? state.travelClass.value : "unknown",
      });
      setServerErrors(response.data.errors.map(e => e.message));

      if (response.data.hasErrors) {
        setIsSaving(false);
        return;
      }

      await props.onSaved();
      setIsSaving(false);
    } catch (e) {
      console.error(e);
      Sentry.captureException(e);
      setIsSaving(false);
      setServerErrors([RESX.GeneralLabels.SomethingUnexpectedHappened]);
    }
  }

  return <ValidatorContext settings={validators.validatorSettings}>
    <div className={classNames("passenger-card-grid", {
      "passenger-card-grid-side-panel-narrow": isNarrow,
      "passenger-card-grid-private-jet": props.isPrivateJet,
    })}>
      <PassengerCardFieldNew
        label=""
        gridArea="passenger"
        render={() => <ContactModalPicker
          canCreateNew={false}
          nonInPageEditing={{
            onChange: (contact) => onValueChange({ contact }),
            startOpen: true,
            value: state.contact,
            validatorKey: "contact",
            required: true,
          }}
        />}
      />
      {!props.isPrivateJet && <PassengerCardFieldNew
        label={RESX.Flight.TravelClass}
        gridArea="travelClass"
        render={() => <TravelClassDropdown
          value={state.travelClass}
          onChange={(travelClass) => onValueChange({ travelClass })}
        />}
      />}
      {!props.isPrivateJet && <PassengerCardFieldNew
        label={RESX.Flight.Seats}
        gridArea="seat"
        render={() => <OptionalTextInput
          value={state.seat}
          onChange={(seat) => onValueChange({ seat })}
          maxLength={serverReference.constants.flight.maxSeatLength}
        />}
      />}
      {!props.isPrivateJet && <PassengerCardFieldNew
        label={RESX.Flight.BookingCode}
        gridArea="bookingCode"
        render={() => <OptionalTextInput
          value={state.bookingCode}
          onChange={(bookingCode) => onValueChange({ bookingCode })}
          maxLength={serverReference.constants.flight.maxBookingReferenceLength}
        />}
      />}
      <PassengerCardFieldNew
        label={RESX.Flight.Meal}
        gridArea="meal"
        render={() => <OptionalTextInput
          value={state.meal}
          onChange={(meal) => onValueChange({ meal })}
          maxLength={serverReference.constants.flight.maxMealLength}
        />}
      />
      {!props.isPrivateJet && <PassengerCardFieldNew
        label={RESX.Flight.ETicket}
        gridArea="eTicket"
        render={() => <OptionalTextInput
          value={state.eTicket}
          onChange={(eTicket) => onValueChange({ eTicket })}
          maxLength={serverReference.constants.flight.maxTicketNumberLength}
        />}
      />}
      <PassengerCardFieldNew
        label={RESX.Flight.Baggage}
        gridArea="baggage"
        render={() => <OptionalTextAreaInput
          value={state.baggage}
          rows={2}
          onChange={(baggage) => onValueChange({ baggage })}
          maxLength={serverReference.constants.flight.maxBaggageLength}
        />}
      />
      <div className="flex gap justify-end" style={{ gridArea: "buttons" }}>
        <ButtonSecondary
          label={RESX.Buttons.btnCancel}
          onClick={() => props.onCancel()}
          disabled={isSaving}
        />
        <ButtonTertiary
          label={RESX.Buttons.Add}
          onClick={save}
          loading={isSaving}
          disabled={isSaving}
        />
      </div>
      {serverErrors.length > 0 && <div style={{ gridArea: "errors" }}>
        <Message color="red">
          {serverErrors[0]}
        </Message>
      </div>}
    </div>
  </ValidatorContext>
}